import { Col, Input, Modal, Row } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import VSpace from '../../common/VSpace';
import { DatacenterReportTypes, DateRanges, getDatacenterReportTypeNames, getFiltersFromJson, ReportFilterViewModel, updateFilterDates } from '../datacenterModel';
import { useCreateFiltersMutation, useDeleteFiltersMutation, useGetFiltersQuery } from '../datacenterQueries';
import ReportFilters from '../ReportFilters';
import ReportSavedFiltersList from '../ReportSavedFiltersList';
import SaveFilterModal from '../SaveFilterModal';
import { projectTypeOptionsFor } from '../../../common/constants';
import useServiceTicketsEnabled from '../../../hooks/useServiceTicketsEnabled';
import { useGetVisitNotesReportDataQuery } from './visitNotesReportQueries';
import { VisitNotesReportData } from './visitNotesReportApi';
import VisitNotesReportFilterDetails from './VisitNotesReportFilterDetails';
import VisitNotesReportList from './VisitNotesReportList';
import { getFilteredItems } from './visitNotesReportModel';

export type VisitNotesReportFilters = {
  startDate: Dayjs;
  endDate: Dayjs;
  searchTerm: string;
  dateRange: DateRanges;
  jobTypes: number[]
}

const VisitNotesReport = () => {
  const showServiceTickets = useServiceTicketsEnabled()
  const projectTypeOptions = projectTypeOptionsFor(showServiceTickets)
  const today = dayjs();
  const defaultStartDate = today.subtract(6, 'month');
  const defaultFilters: VisitNotesReportFilters = {
    startDate: defaultStartDate,
    endDate: today,
    searchTerm: '',
    dateRange: 'custom',
    jobTypes: []
  }

  const [open, setOpen] = useState(false);
  const [deleteFilterModalConfirm, deleteFiltersModalContext] = Modal.useModal();
  const [filterName, setFilterName] = useState('');
  const [filters, setFilters] = useState<VisitNotesReportFilters>(defaultFilters);
  const { startDate, endDate } = filters;

  const getVisitNotesReportDataQuery = useGetVisitNotesReportDataQuery(
    startDate.toISOString().split('T')[0],
    endDate.toISOString().split('T')[0]
  );
  const getFiltersQuery = useGetFiltersQuery(DatacenterReportTypes.VisitNotes)
  const createFiltersMutation = useCreateFiltersMutation()
  const deleteFiltersMutation = useDeleteFiltersMutation()

  const filtersList: ReportFilterViewModel[] = getFiltersQuery.data?.map((x) => {
    return {
      ...x,
      filters: getFiltersFromJson(x.filters)
    }
  }).sort((a, b) => a.order - b.order) || [];

  const jobTypesSet = new Set(filters.jobTypes)
  const filteredVisitNotesData: VisitNotesReportData[] = getFilteredItems(getVisitNotesReportDataQuery.data || [], filters.searchTerm.toLowerCase(), jobTypesSet)

  const onSaveFilter = () => {
    createFiltersMutation.mutate({
      request: {
        reportId: DatacenterReportTypes.VisitNotes,
        name: filterName,
        reportTypeName: getDatacenterReportTypeNames(DatacenterReportTypes.VisitNotes),
        filters: updateFilterDates(filters)
      },
      reportType: DatacenterReportTypes.VisitNotes
    });
    setOpen(false);
    setFilterName('');
  }

  return (<Row gutter={16}>
    {
      (filtersList.length > 0) && <Col span={4}>
        <ReportSavedFiltersList
          filtersList={filtersList}
          onFilterSelect={(filters) => setFilters(filters as VisitNotesReportFilters)}
          onDeleteFilters={(selectedFiltersIds) => {
            deleteFilterModalConfirm.confirm({
              title: 'Do you want to delete the selected filters?',
              content: 'This action cannot be undone',
              onOk: () => {
                deleteFiltersMutation.mutate({ filtersIds: selectedFiltersIds, reportType: DatacenterReportTypes.VisitNotes })
              },
              okText: 'Delete',
              okButtonProps: {
                loading: deleteFiltersMutation.isLoading,
                danger: true
              }
            })
          }}
          renderFilterDetails={(filterItem) => <VisitNotesReportFilterDetails filterItem={filterItem} />}
          reportType={DatacenterReportTypes.VisitNotes}
        />
      </Col>
    }

    <Col span={filtersList.length > 0 ? 20 : 24}>
      <VSpace>
        <Input.Search placeholder="Filter by job name or customer name" value={filters.searchTerm} onChange={e => setFilters({ ...filters, searchTerm: e.target.value })} />
        <ReportFilters
          dateRangeTitle='Visit Date'
          filters={filters}
          onFiltersChange={setFilters}
          isDataLoading={getVisitNotesReportDataQuery.isLoading}
          defaultFilters={defaultFilters}
          onSaveFilters={() => {
            setOpen(true)
          }}
          selectOptions={{
            jobTypes: {
              mode: 'multiple',
              options: projectTypeOptions,
              placeholder: 'Select Job Types',
              style: { width: 300 },
              allowClear: true,
            }
          }}
        />
        <VisitNotesReportList visitNotes={filteredVisitNotesData} dateRange={{ start: startDate, end: endDate }} />
      </VSpace>
    </Col>

    <SaveFilterModal
      inputValue={filterName}
      onCancel={() => {
        setFilterName('');
        setOpen(false);
      }}
      onInputChange={setFilterName}
      onOk={onSaveFilter}
      open={open}
      title='Save the Filter'
      okText='Save Filter'
      cancelText='Cancel'
    />
    {deleteFiltersModalContext}
  </Row>
  )
}

export default VisitNotesReport